import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../states/hooks";
import {
  fetchClasses,
  createClass,
  updateClass,
  deleteClass,
  clearError,
  selectClasses,
  selectClassesLoading,
  selectClassesError,
} from "../../states/classes/ClassesSlice";
import { ClassData } from "../../model/ClassData";
import { useBase } from "../../context/BaseContext";
import { useAuth } from "../../context/AuthContext";
import { canManageSchool, isAdmin } from "../../common/enum/Role";
import {
  fetchAllSchools,
  selectAllSchools,
} from "../../states/schools/SchoolsSlice";
import EditClassModal from "../../components/EditClassModal";
import { EditIcon, DeleteIcon, EmptyBoxIcon } from "../../assets/svg";
import "../dashboard/Dashboard.css";

const ClassesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const classes = useAppSelector(selectClasses);
  const isLoading = useAppSelector(selectClassesLoading);
  const error = useAppSelector(selectClassesError);
  const { showError, showConfirm, showSuccess } = useBase();
  const { user } = useAuth();
  const admin = isAdmin(user?.role);
  const canManage = canManageSchool(user?.role);
  const schools = useAppSelector(selectAllSchools);
  // Admin-only school filter; "" means every school.
  const [schoolFilter, setSchoolFilter] = useState("");

  const [modal, setModal] = useState<{
    isOpen: boolean;
    classData: ClassData | null;
  }>({ isOpen: false, classData: null });

  useEffect(() => {
    if (admin) dispatch(fetchAllSchools());
  }, [dispatch, admin]);

  useEffect(() => {
    dispatch(fetchClasses(schoolFilter || undefined));
  }, [dispatch, schoolFilter]);

  const schoolName = (id?: string) =>
    schools.find((s) => s.id === id)?.name || "";

  useEffect(() => {
    if (error) {
      showError("Lỗi", error);
      dispatch(clearError());
    }
  }, [error, showError, dispatch]);

  const handleSave = async (data: { name: string; schoolId?: string }) => {
    if (modal.classData) {
      const result = await dispatch(
        updateClass({ id: modal.classData.id, name: data.name }),
      );
      if (updateClass.fulfilled.match(result)) {
        showSuccess("Thành công", "Đã cập nhật lớp");
      }
    } else {
      const result = await dispatch(createClass(data));
      if (createClass.fulfilled.match(result)) {
        showSuccess("Thành công", "Đã tạo lớp mới");
        // Keep the filtered list consistent with the new class's school.
        if (schoolFilter && data.schoolId !== schoolFilter) {
          dispatch(fetchClasses(schoolFilter));
        }
      }
    }
    setModal({ isOpen: false, classData: null });
  };

  const handleDelete = (c: ClassData) => {
    showConfirm(
      "Xoá lớp",
      `Xoá lớp "${c.name}"? Toàn bộ học sinh và lịch sử điểm danh sẽ bị xoá.`,
      async () => {
        const result = await dispatch(deleteClass(c.id));
        if (deleteClass.fulfilled.match(result)) {
          showSuccess("Thành công", "Đã xoá lớp");
        }
      },
    );
  };

  return (
    <>
      <EditClassModal
        isOpen={modal.isOpen}
        classData={modal.classData}
        schools={admin ? schools : undefined}
        defaultSchoolId={schoolFilter}
        onClose={() => setModal({ isOpen: false, classData: null })}
        onSave={handleSave}
      />
      <div className="dashboard-container">
        <div className="dashboard-stats-grid">
          <div className="dashboard-stat-card">
            <div className="dashboard-stat-card-content">
              <div className="dashboard-stat-flex">
                <div className="dashboard-stat-info">
                  <dl>
                    <dt className="dashboard-stat-label">Tổng số lớp</dt>
                    <dd className="dashboard-stat-value">{classes.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          {admin ? (
            <select
              value={schoolFilter}
              onChange={(e) => setSchoolFilter(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                height: "fit-content",
                alignSelf: "center",
                outline: "none",
              }}
            >
              <option value="">Tất cả trường</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          ) : (
            <div />
          )}
          {canManage ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setModal({ isOpen: true, classData: null })}
              sx={{
                textTransform: "none",
                height: "fit-content",
                alignSelf: "center",
              }}
            >
              + Tạo lớp mới
            </Button>
          ) : (
            <div />
          )}
        </div>

        <div className="dashboard-table-container">
          {isLoading ? (
            <div className="dashboard-loading">
              <div className="dashboard-loading-content">
                <div className="dashboard-loading-spinner" />
                <p className="dashboard-loading-text">Đang tải lớp...</p>
              </div>
            </div>
          ) : classes.length === 0 ? (
            <div className="dashboard-empty">
              <EmptyBoxIcon className="dashboard-empty-icon" />
              <h3 className="dashboard-empty-title">Chưa có lớp nào</h3>
              {canManage && (
                <p className="dashboard-empty-description">
                  Bấm "Tạo lớp mới" để bắt đầu.
                </p>
              )}
            </div>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
              <Table aria-label="classes table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>Tên lớp</TableCell>
                    {admin && (
                      <TableCell sx={{ fontWeight: "bold" }}>Trường</TableCell>
                    )}
                    <TableCell sx={{ fontWeight: "bold" }}>Ngày tạo</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="center">
                      Hành động
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {classes.map((c) => (
                    <TableRow
                      key={c.id}
                      sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                    >
                      <TableCell>
                        <Typography sx={{ fontWeight: 500 }}>
                          {c.name}
                        </Typography>
                      </TableCell>
                      {admin && <TableCell>{schoolName(c.schoolId)}</TableCell>}
                      <TableCell>
                        {new Date(c.createdAt).toLocaleDateString("vi-VN")}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              navigate(`/classes/${c.id}/students`)
                            }
                            sx={{ textTransform: "none" }}
                          >
                            Học sinh
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={() =>
                              navigate(`/classes/${c.id}/attendance`)
                            }
                            sx={{ textTransform: "none" }}
                          >
                            Điểm danh
                          </Button>
                          {canManage && (
                            <>
                              <IconButton
                                onClick={() =>
                                  setModal({ isOpen: true, classData: c })
                                }
                                color="primary"
                                size="small"
                                title="Sửa"
                              >
                                <EditIcon style={{ width: 20, height: 20 }} />
                              </IconButton>
                              <IconButton
                                onClick={() => handleDelete(c)}
                                color="error"
                                size="small"
                                title="Xoá"
                              >
                                <DeleteIcon style={{ width: 20, height: 20 }} />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </div>
    </>
  );
};

export default ClassesPage;
